import { Router } from '@angular/router';
import { LogoutService } from './../../login/services/logout.service';
import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SwalService {
  /**
   * Se pasó de la versión 8 de sweetalerts2 a la 9
   * Breaking Changes: https://github.com/sweetalert2/sweetalert2/releases/tag/v9.0.0
   */

  notification = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3500,
    timerProgressBar: true,
    onOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });

  constructor(private _logout: LogoutService, private _router: Router) {}

  /**
   * Función para disparar un modal de éxito personalizable.
   * Es parámetro onClose convierte al modal el una promesa (.then)
   * @param title mensaje a mostrar
   */
  showPromiseSuccessModal(title, text?) {
    return Swal.fire({
      icon: 'success',
      title: title,
      text: text,
      onClose: () => {},
    });
  }

  /**
   * Función para disparar un modal de éxito personalizable.
   * Es parámetro onClose convierte al modal el una promesa (.then)
   * @param message mensaje a mostrar
   */
  showPromiseErrorModal(title, text?) {
    return Swal.fire({
      icon: 'error',
      title: title,
      text: text,
      onClose: () => {},
    });
  }

  /**
   * Función para disparar un modal de éxito personalizable.
   * @param message mensaje a mostrar
   */
  showSuccessModal(title, message?) {
    return Swal.fire({
      title: title,
      text: message,
      icon: 'success',
    });
  }

  /**
   * Función para disparar un modal de error personalizable.
   * @param title Título del mensaje de error
   * @param message mensaje de error a mostrar
   */
  showErrorModal(title, message?) {
    return Swal.fire({
      title: title,
      text: message,
      icon: 'error',
    });
  }

  /**
   * Función para disparar un modal de alerta personalizable.
   * @param title Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showWarningModal(title, message?) {
    return Swal.fire({
      title: title,
      text: message,
      icon: 'warning',
    });
  }

  /**
   * Función para disparar una notificación personalizable.
   * @param title Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showSuccessNotification(title, message?) {
    return this.notification.fire({
      title: title,
      text: message,
      icon: 'success',
    });
  }

  /**
   * Función para disparar una notificación personalizable.
   * @param title Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showWarningNotification(title, message?) {
    return this.notification.fire({
      title: title,
      text: message,
      icon: 'warning',
    });
  }

  /**
   * Función para disparar una notificación personalizable.
   * @param title Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showErrorNotification(title: string, message?: string) {
    return this.notification.fire({
      title: title,
      text: message,
      icon: 'error',
    });
  }

  /**
   *
   */
  showCapa8Modal(error: HttpErrorResponse, title?, message?) {
    return Swal.fire({
      icon: 'error',
      title: title,
      text: message,
      confirmButtonText: 'Entiendo',
      html: `${JSON.stringify(error.error)}`,
      footer: 'Por favor, intenta iniciar sesión nuevamente o contacta a Soporte Técnico',
    }).then((result) => {
      if (result.value) {
        localStorage.clear();
        this._router.navigate(['']);
      }
    });
  }

  /**
   * Función para disparar un modal de confirmación personalizable.
   * Dependiendo de la opción seleccionada "Confirmar" o "Cancelar"
   * pueden realizar alguna acción llamando al método .then tras la respuesta de esta función.
   *
   * @param msgTitle Título de la pregunta para el usuario
   * @param message Un mensaje, puede dejarse como string vacío
   * @param acceptText Texto del botón de "confirmar"
   * @param cancelText Texto en el botón de "cancelar"
   * @returns Un objeto de tipo Promise<> tras la acción del usuario.
   *
   * Si pulsa "Confirm", el json que se obtendrá será de la forma {result: true}.
   * Si pulsa "Cancel", el JSON que se obtendrá será {dissmiss: value}, siendo value la forma de cancelación, y para este caso: "cancel"
   */
  showStyledConfirmationModal(msgTitle, message, acceptText, cancelText) {
    return Swal.fire({
      title: msgTitle,
      text: message,
      icon: 'question',
      confirmButtonText: acceptText,
      showCancelButton: true,
      cancelButtonText: cancelText,
      heightAuto: false,
      /**
       * Si se quieren estilos personalizados y usar clases
       * descomentar las líneas de abajo
       */
      // buttonsStyling: false,
      // customClass: {
      //   confirmButton: 'btn btn-primary',
      //   cancelButton: 'btn btn-danger',
      // },
    });
  }

  /**
   * Funcion para chequear el error y mostrar texto segun sea el error
   * @param error el error que llega de la suscripcion en el componente
   */
  errorHandler(error: HttpErrorResponse) {
    console.log(error.error);
    if (error.status == 0) {
      this.showErrorNotification('La sesión expiró', 'Por favor, inicia sesión nuevamente.');
      this._logout.logOut();
    } else if (error.status == 400) {
      const err = error.error;

      switch (Object.keys(err)[0]) {
        case 'errorFechasiguales':
          this.showWarningModal('Error en la fecha', 'No puedes colocar una hora o fecha anterior a la actual.');
          break;

        case 'errorCitaUser':
          this.showWarningModal(
            'No puedes modificar la cita',
            'Esta cita está gestionada por otra persona, solo ella la puede modificar'
          );
          break;

        case 'errorCitaUser':
          this.showWarningModal(
            'No puedes modificar la cita',
            'Esta cita está gestionada por otra persona, solo ella la puede modificar'
          );
          break;

        case 'errorCitaCompras':
          this.showWarningModal(
            'No puedes modificar la cita',
            'Esta cita está gestionada por otra persona, solo ella la puede modificar'
          );
          break;

        case 'errorCitaNoGestionada':
          this.showWarningModal(
            'No puedes modificar la cita',
            'Esta cita está gestionada por otra persona, solo ella la puede modificar'
          );
          break;

        case 'citaVencidaPaahoy':
          this.showWarningModal('Cita expirada', 'La cita ya ha vencido.');
          break;

        case 'errorCitaFechaVencida':
          this.showWarningModal('Cita expirada', 'Esta cita ya ha expirado, solo podrás posponerla o cancelarla');
          break;

        case 'numDocumentoRepetido':
          this.showWarningModal('Ya existe un documento con este número', '¿Has colocado correctamente el número?');
          break;

        case 'errorFechasiguales':
          this.showWarningModal('Error en la fecha', 'No puedes colocar una hora o fecha anterior a la actual.');
          break;

        case 'errorFechasiguales':
          this.showWarningModal('Error en la fecha', 'No puedes colocar una hora o fecha anterior a la actual.');
          break;

        default:
          this.showCapa8Modal(error, 'Ocurrió un error', 'Copia el error y mándalo a Soporte Técnico');
          break;
      }
    } else if (error.status === 401) {
      this._logout.logOut();
      this.showErrorNotification('Por favor, intenta iniciar sesión de nuevo');
    } else if (error.status === 422) {
      this.showErrorModal('La orden de compra debe ser única', '');
    } else if (error.status == 500) {
      this.showErrorModal('Ocurrió este error en el servidor', error.error.message);
    }
  }
}
